// Pure aggregation helpers for the Updates hub. Kept side-effect-free and
// heavily unit-tested; the component is a thin shell over these.

import type { SiteStatus, JobStatus } from '../../lib/types';

export interface AggregatedSite {
  siteId: string;
  currentVersion: string;
  targetVersion: string;
}

/** One expandable row: a plugin slug with the sites that have it pending. */
export interface AggregatedUpdate {
  slug: string;
  name: string;
  isEm: boolean;
  /** Newest target version seen across sites (they should agree via PUC). */
  targetVersion: string;
  siteCount: number;
  sites: AggregatedSite[];
}

export function aggregatePendingUpdates(statuses: SiteStatus[]): AggregatedUpdate[] {
  const bySlug = new Map<string, AggregatedUpdate>();

  for (const status of statuses) {
    for (const p of status.plugins) {
      if (!p.updateVersion) continue;
      let row = bySlug.get(p.slug);
      if (!row) {
        row = {
          slug: p.slug,
          name: p.name,
          isEm: p.isEm,
          targetVersion: p.updateVersion,
          siteCount: 0,
          sites: [],
        };
        bySlug.set(p.slug, row);
      }
      row.sites.push({
        siteId: status.siteId,
        currentVersion: p.version,
        targetVersion: p.updateVersion,
      });
      row.siteCount = row.sites.length;
      if (p.updateVersion > row.targetVersion) {
        row.targetVersion = p.updateVersion;
      }
    }
  }

  return [...bySlug.values()].sort((a, b) => a.slug.localeCompare(b.slug));
}

/** A site with core pending, for the hub's core section. */
export interface CoreUpdateRow {
  siteId: string;
  currentVersion: string;
  targetVersion: string;
}

// No fleet-wide THEME section, deliberately: the parent theme is PUC-managed
// and updated by a no-argument call, so a fleet table would just be one button
// per site — exactly the per-site Updates tab. Theme stays per-site (P1).
export function aggregateCoreUpdates(statuses: SiteStatus[]): CoreUpdateRow[] {
  return statuses
    .filter((s) => Boolean(s.coreUpdateVersion))
    .map((s) => ({
      siteId: s.siteId,
      currentVersion: s.coreVersion,
      targetVersion: s.coreUpdateVersion as string,
    }))
    .sort((a, b) => a.siteId.localeCompare(b.siteId));
}

/** Selection key for one (plugin, site) pair. */
export function pairKey(slug: string, siteId: string): string {
  return `${slug}|${siteId}`;
}

export type TriState = 'all' | 'some' | 'none';

/** How much of a plugin row is selected — drives the tri-state parent box. */
export function rowSelectionState(row: AggregatedUpdate, selected: Set<string>): TriState {
  const hits = row.sites.filter((s) => selected.has(pairKey(row.slug, s.siteId))).length;
  if (hits === 0) return 'none';
  return hits === row.sites.length ? 'all' : 'some';
}

/** Case-insensitive match on plugin slug/name OR any of its site ids. */
export function rowMatchesFilter(row: AggregatedUpdate, filter: string): boolean {
  const q = filter.trim().toLowerCase();
  if (!q) return true;
  if (row.slug.toLowerCase().includes(q)) return true;
  if (row.name.toLowerCase().includes(q)) return true;
  return row.sites.some((s) => s.siteId.toLowerCase().includes(q));
}

export interface UpdateFilters {
  text: string;
  emOnly: boolean;
}

export function filterRows(rows: AggregatedUpdate[], filters: UpdateFilters): AggregatedUpdate[] {
  return rows.filter(
    (r) => (!filters.emOnly || r.isEm) && rowMatchesFilter(r, filters.text),
  );
}

// Grouped by site so a plugin can be updated on one site without touching
// others — row-only selection couldn't express that.
export function buildPairRequest(
  rows: AggregatedUpdate[],
  selected: Set<string>,
): { updates: Array<{ siteId: string; slugs: string[] }>; kind: 'updates.plugins' } {
  const bySite = new Map<string, string[]>();
  for (const row of rows) {
    for (const site of row.sites) {
      if (!selected.has(pairKey(row.slug, site.siteId))) continue;
      const slugs = bySite.get(site.siteId) ?? [];
      slugs.push(row.slug);
      bySite.set(site.siteId, slugs);
    }
  }
  return {
    updates: [...bySite.entries()].map(([siteId, slugs]) => ({ siteId, slugs })),
    kind: 'updates.plugins',
  };
}

/** Every pair across the given rows — the select-all target. */
export function allPairs(rows: AggregatedUpdate[]): string[] {
  return rows.flatMap((r) => r.sites.map((s) => pairKey(r.slug, s.siteId)));
}

const JOB_LABELS: Record<JobStatus, string> = {
  queued: 'Queued',
  running: 'Running…',
  succeeded: 'Completed',
  failed: 'Failed',
  partial: 'Partial (some sites failed)',
};

export function jobProgressLabel(status: JobStatus): string {
  return JOB_LABELS[status];
}

export function isJobTerminal(status: JobStatus): boolean {
  return status === 'succeeded' || status === 'failed' || status === 'partial';
}
